import cx from 'classnames'
import moment from 'moment/moment'
import { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'

import { getTimeline } from '../../../selectors/timeline'
import { useAppDispatch, useAppSelector } from '../../../store'
import { encounterReset } from '../../Encounter/actions'
import { init as encounterHistoryInit } from '../../Encounter/EncountersHistory/actions'
import { FriendlyDateTime } from '../../shared/Date/friendly-date-time'
import FriendlyDate from '../../shared/Date/FriendlyDate'
import { CircularProgress } from '../../shared/NarusMaterial'

import './styles.scss'

interface Item {
  title: string | 'Grand Rounds' | 'Monthly Summary' | 'APS' | 'Research'
  datetime: string
  type: 'encounter' | string
  patientId: string
  edited?: boolean
  grandRoundsReportId: string
  monthlySummaryReportId: string
  apsReportId: string
  researchReviewReportId: string
  encounterSummaryReportId: string
}

export function TimelineV2 (): JSX.Element {
  const params = useParams() as {id: string, clientName: string}
  const dispatch = useAppDispatch()

  const { encountersHistory, loading, encounter, timelineItems } = useAppSelector(redux => ({
    encountersHistory: redux.encountersHistory,
    // @ts-expect-error // it's okay
    loading: redux?.patient?.loading ?? false,
    encounter: redux.encounter,
    timelineItems: getTimeline(redux)
  }))

  useEffect(() => {
    if (encounter?.signed && params.id) {
      dispatch(encounterReset())
      void dispatch(encounterHistoryInit(params.id))
    }
  }, [ dispatch, encounter?.signed, params.id ])

  // @ts-expect-error it's okay
  if (encountersHistory.loading || loading) {
    return (
      <div className='timeline-container'>
        <ul className='timeline'>
          <CircularProgress />
        </ul>
      </div>
    )
  }
  return (
    <div className='timeline-container'>
      <ul className='timeline'>
        {timelineItems.map((item, index) => (
          <RenderItem
            item={item}
            key={index}
          />
        ))}
      </ul>
    </div>
  )
}

interface RenderItemProps {
  item: Item
}

function RenderItem ({ item }: RenderItemProps): JSX.Element {
  const params = useParams() as {clientName: string}
  const { datetime, title } = item

  const today = moment().startOf('day')
  const isToday = moment(datetime).isSame(today, 'day')

  const itemClass = cx({
    'timeline-item': true,
    'today': isToday
  })

  if (item.type !== 'encounter') {
    return (
      <li
        className={itemClass}
      >
        <div>
          <div className='timestamp'>
            <FriendlyDate
              date={datetime}
              short
            />
          </div>
          <div className='title'>{title}</div>
        </div>
      </li>
    )
  }

  if (title === 'Grand Rounds' || title === 'Monthly Summary' || title === 'APS' || title === 'Research') {
    let endLink = ''
    let displayTitle = null
    if (item.grandRoundsReportId) {
      endLink = `${item.grandRoundsReportId}/grandRounds`
    } else if (item.researchReviewReportId) {
      displayTitle = 'Research Report'
      endLink = `${item.researchReviewReportId}/researchReview`
    } else if (item.apsReportId) {
      displayTitle = 'APS Report'
      endLink = `${item.apsReportId}/aps`
    } else if (item.monthlySummaryReportId) {
      displayTitle = 'Care Management Summary'
      endLink = `${item.monthlySummaryReportId}/monthlySummary`
    }

    return (
      <li className={itemClass}>
        <Link to={`/patient/${params.clientName}/${item.patientId}/report/${endLink}`}>
          <div>
            <div className='timestamp'>
              <FriendlyDateTime
                datetime={datetime}
                short
              />
            </div>
            <div className='title'>{displayTitle ?? title}</div>
          </div>
        </Link>
      </li>
    )
  }

  return (
    <li className={itemClass}>
      <Link
        to={`/patient/${params.clientName}/${item.patientId}/report/${item.encounterSummaryReportId}/encounterSummary`}
      >
        <div>
          <div className='timestamp'>
            <FriendlyDateTime
              datetime={datetime}
              short
            />
          </div>
          <div className='title'>
            {title}
            {!!item.edited && (
              <div
                className='svg-small-edit-icon'
                style={{
                  float: 'right',
                  marginLeft: '10px'
                }}
              />
            )}
          </div>
        </div>
      </Link>
    </li>
  )
}
